import { GCInstant, yahooAdsPlugin } from 'src/lib/gcinstant';
import { now, user } from 'src/StateObserver';
import { devSettings } from '../../lib/settings';
import { canConsumeAds } from 'src/replicant/ads/ads.getters';
import ruleset from 'src/replicant/ads/ads.ruleset';
import { hasPurchasedRecently } from 'src/lib/stateUtils';
import { retry } from 'src/lib/utils';
import { captureGenericError } from 'src/lib/sentry';
import features from 'src/replicant/ruleset/features';
import { AdShowAnalytics, isInLineMiniApp } from '@play-co/gcinstant';
import { hasJoinedLeague } from '../../replicant/match2/leagues/leagues.getters';
import { Feature } from 'src/replicant/analytics';
import { isDynamicTestInBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import type { State } from 'src/replicant/State';

/** Minimum puzzle level for ads, unless the player is allow-listed. */
const ADS_UNLOCK_LEVEL = 160;

/**
 * For Native apps only
 */
const adPlacementIds = {
  rewardedVideo: 'ca-app-pub-2550261217530119/4854870656',
};

/**
 * Yahoo Ads placement IDs for mini app only
 */
const yahooAdPlacementIds = {
  teens: '60905_981084', // BD_Teen_リワード
  adults: '43164_981085', // BD_Adult_リワード
  unknown: '103853_981086', // BD_Unknown_リワード
};

/**
 * Returns true when rewarded video ads should be considered available.
 * Dev overrides take precedence; otherwise defers to the GCInstant flag.
 */
function areRewardedVideoAdsEnabled() {
  if (devSettings.get('simulateAds') || devSettings.get('bypassAdGating')) {
    return true;
  }

  // Ads are disabled once the player has joined a league.
  if (hasJoinedLeague(user())) {
    return false;
  }

  // Check for Yahoo Ads in LINE Mini App
  if (isYahooAdsAvailable()) {
    return true;
  }

  return GCInstant.canShowRewardedVideoAds;
}

/**
 * Determines whether the player passes the preload-specific eligibility gates.
 *
 * @param state Player state used to evaluate ad eligibility.
 * @returns Whether ads may be loaded for the player.
 */
function shouldPreloadAds(state: State): boolean {
  // The ads feature flag remains authoritative for preloading.
  if (!features(state).ads) {
    return false;
  }

  // Players must remain within their ad consumption limits.
  if (!canConsumeAds(state, now())) {
    return false;
  }

  // Recent purchases suppress ads unless the player is allow-listed.
  if (isBlockedByPurchase()) {
    return false;
  }

  // Allow-listed players bypass every preload-specific gate.
  if (
    isDynamicTestInBucket(state, ab.tests.FEATURE_ADS_ALLOW_LIST, 'enabled')
  ) {
    return true;
  }

  // Players must reach the ads unlock level before loading ads.
  if (state.match2.level < ADS_UNLOCK_LEVEL) {
    return false;
  }

  // The player passes every preload-specific eligibility gate.
  return true;
}

/**
 * Single entry point for rewarded video preload. Gates on whether ads are
 * enabled and the player is eligible, then runs the provider chain with retry
 * and error logging.
 *
 * Fire-and-forget by design — the SDK preload is observably void, so callers
 * have no useful promise to await. The `.catch()` keeps Sentry logging and
 * prevents unhandled rejections.
 */
export function preloadRewardedVideoAd(trigger: PreloadTrigger) {
  // Rewarded video ads must be available from a provider.
  if (!areRewardedVideoAdsEnabled()) return;

  // Players should not load ads they are ineligible to see.
  if (!shouldPreloadAds(user())) return;

  // Start the provider preload flow with retry safeguards.
  void retry(() => preloadRewardedVideoAdAsync(trigger), {
    // Retry up to 5 times.
    retryCount: 5,
    // Wait 1 second between retries.
    linearBackoff: 1001,
    // Don't retry if the user closes the ad before it completes.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    retryOnlyIf: (e: any) =>
      e.code !== 'USER_INPUT' && e.code !== 'INVALID_OPERATION',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }).catch((e: any) => {
    if (shouldIgnoreAdError(e)) return;
    captureGenericError('Error preloading rewarded ad: ', e);
  });
}

/**
 * Preloads a rewarded video ad using the appropriate provider based on availability.
 */
async function preloadRewardedVideoAdAsync(trigger: PreloadTrigger) {
  if (isYahooAdsAvailable()) {
    return issueYahooPreload(trigger);
  }

  if (trigger !== 'session-start') return;

  // Fallback to GCInstant for Native apps
  return GCInstant.ads.preloadRewardedVideoAdAsync(
    adPlacementIds.rewardedVideo,
  );
}

/**
 * Displays a preloaded rewarded video ad with retry safeguards and
 * analytics reporting.
 */
export async function showPreloadedRewardedVideoAdAsync(
  analytics: AdShowAnalytics,
) {
  if (isYahooAdsAvailable()) {
    // Yahoo's showAd consumes the preload regardless of outcome — clear the
    // warm slot so the next trigger may issue a fresh preload.
    preloadPending = false;
    return yahooAdsPlugin?.showAd(getYahooAdPlacementId(), {
      ...analytics,
      adType: 'rewardedVideo',
    });
  }

  return GCInstant.ads.showPreloadedRewardedVideoAdAsync(analytics);
}

/**
 * Determines whether a rewarded video ad should be offered to the player.
 * Honors developer overrides, device-specific restrictions, feature flags,
 * ad consumption limits, recent purchase blocks, and loader readiness.
 */
export function canShowRewardedVideoAds(): boolean {
  // Dev override: bypass most gating checks (still requires loader/provider readiness)
  if (devSettings.get('bypassAdGating')) {
    console.warn(
      'Bypassing ad gating due to dev settings',
      isRewardedVideoAdLoaded(),
    );
    return isRewardedVideoAdLoaded();
  }

  // Don't show rewarded video ads on iOS app if the native version is 2.11.0
  if (
    GCInstant.osType === 'IOS_APP' &&
    GCInstant.legacyNative?.bridge?.app?.version === '2.11.0'
  ) {
    return false;
  }

  // Allow ads if the user is in dev ads simulation mode
  if (devSettings.get('simulateAds')) {
    return features(user()).ads && canConsumeAds(user(), now());
  }

  // Production behavior
  return (
    features(user()).ads &&
    areRewardedVideoAdsEnabled() &&
    canConsumeAds(user(), now()) &&
    !isBlockedByPurchase() &&
    isRewardedVideoAdLoaded()
  );
}

/**
 * Determines if a rewarded video ad is ready for immediate display.
 * In simulation mode it always reports true; otherwise it checks the
 * underlying GCInstant loader state.
 */
function isRewardedVideoAdLoaded(): boolean {
  if (devSettings.get('simulateAds')) {
    return true;
  }

  if (isYahooAdsAvailable()) {
    return true;
  }

  if (GCInstant.canShowRewardedVideoAds) {
    return GCInstant.ads.rewardedVideoAdIsLoaded();
  }

  return false;
}

/**
 * Checks whether Yahoo Ads can be shown in the current LINE Mini App context.
 * Requires LINE Mini App environment, Yahoo plugin availability, and feature flag.
 */
function isYahooAdsAvailable(): boolean {
  return !!yahooAdsPlugin && isInLineMiniApp() && yahooAdsPlugin.isAvailable();
}

/**
 * Retrieves the Yahoo Ads placement ID corresponding to the user's demographic group.
 * Falls back to the unknown placement when the group cannot be classified.
 */
function getYahooAdPlacementId(): string {
  const isAdult = GCInstant.getDemographics()?.isAdult;

  if (isAdult === false) {
    return yahooAdPlacementIds.teens;
  }

  if (isAdult === true) {
    return yahooAdPlacementIds.adults;
  }

  return yahooAdPlacementIds.unknown;
}

/**
 * Returns the total number of extra moves available across all ad continue streaks.
 */
export function getMaximumAdContinueMoves(): number {
  return ruleset.adContinueMoves.reduce((a, b) => a + b, 0);
}

/**
 * Returns the number of additional moves granted for a given ad continue streak.
 *
 * @param streakNum The streak index for the ad continue reward.
 * @returns The number of additional moves awarded for the streak.
 */
export function getAdContinueMoves(streakNum: number): number {
  return ruleset.adContinueMoves[streakNum] ?? 0;
}

/**
 * Checks ad readiness, unlock level, and the once-per-attempt continue limit.
 *
 * @param state Player state used for the unlock level and allow-list checks.
 * @param adContinueMoves Ad continue moves already granted this attempt.
 * @returns Whether to offer an ad continue, including the allow-list probability.
 */
export function shouldOfferAdContinue(
  state: Pick<State, 'match2' | 'ruleset'>,
  adContinueMoves: number,
): boolean {
  // Allow-listed players bypass the level gate but retain their offer probability.
  const adsAllowListed = isDynamicTestInBucket(
    state,
    ab.tests.FEATURE_ADS_ALLOW_LIST,
    'enabled',
  );
  const adsLockedByLevel =
    !adsAllowListed && state.match2.level < ADS_UNLOCK_LEVEL;

  // Require available ads and the unlock level before offering a continue.
  let adContinue =
    canShowRewardedVideoAds() &&
    // Only show the ad continue once per level.
    adContinueMoves === 0 &&
    !adsLockedByLevel;

  // Roll only for otherwise eligible allow-listed players.
  if (adContinue && adsAllowListed) {
    adContinue = Math.random() < ruleset.allowListedAdContinueProbability;
  }

  // Return the final offer eligibility.
  return adContinue;
}

/**
 * Determines whether rewarded ads should be blocked because the user
 * has made a recent purchase. The time window is defined by
 * ruleset.purchaseThreshold.
 *
 * @returns {boolean} True if a recent purchase should suppress ads.
 */
function isBlockedByPurchase(): boolean {
  if (
    isDynamicTestInBucket(user(), ab.tests.FEATURE_ADS_ALLOW_LIST, 'enabled')
  ) {
    return false;
  }

  return hasPurchasedRecently(ruleset.purchaseThreshold);
}

/**
 * True if error should be ignored and not reported to Sentry.
 * @param e
 * @returns
 */
export function shouldIgnoreAdError(e: any) {
  if (e.message?.includes('No ad to show')) {
    return true;
  }

  if (e.code === 'USER_INPUT') {
    return true;
  }

  return false;
}

// ============================================================================
// Yahoo ad preload
// ============================================================================

type PreloadTrigger = 'session-start' | 'puzzle-start' | 'post-ad-show';

const YAHOO_PRELOAD_TRIGGERS: ReadonlySet<PreloadTrigger> = new Set([
  'puzzle-start',
  'post-ad-show',
]);

/**
 * One warm preload at a time. Set true after a successful preloadAd, set
 * false in showPreloadedRewardedVideoAdAsync (Yahoo consumes the preload on
 * every showAd attempt). Prevents wasted impressions when the user wins
 * many puzzles in a row without ever showing an ad.
 */
let preloadPending = false;

async function issueYahooPreload(trigger: PreloadTrigger): Promise<void> {
  if (!YAHOO_PRELOAD_TRIGGERS.has(trigger)) return;
  if (!canShowRewardedVideoAds()) return;

  if (devSettings.get('simulateAds')) return;
  if (preloadPending) return;

  try {
    // getYahooAdPlacementId() calls GCInstant.getDemographics(), which throws
    // if called before GCInstant's analytics storage has loaded. Awaiting
    // waitForEntryAnalytics() guarantees storage is ready before we resolve
    // the placement ID. (Ideally getYahooAdPlacementId would await this
    // itself to make the dependency explicit, but that's out of scope here.)
    await GCInstant.waitForEntryAnalytics();

    // Claim the slot only after analytics gating succeeds; otherwise a failed
    // wait would permanently block future preloads and short-circuit retries.
    // Cleared again when showPreloadedRewardedVideoAdAsync runs.
    // eslint-disable-next-line require-atomic-updates
    preloadPending = true;

    yahooAdsPlugin?.preloadAd(getYahooAdPlacementId(), {
      feature: Feature.AD._,
      subFeature: Feature.AD.REWARDED_VIDEO,
      adType: 'rewardedVideo',
    });
  } catch (error) {
    // eslint-disable-next-line require-atomic-updates
    preloadPending = false;
    throw error;
  }
}
